import { Routes, Route, Navigate } from "react-router-dom"
import { Header } from "@/components/layout/Header"
import { useAuth } from "@/contexts/AuthContext"

// Pages - lazy loaded for code splitting
import { lazy, Suspense } from "react"

const Home = lazy(() => import("@/pages/Home"))
const Explore = lazy(() => import("@/pages/Explore"))
const TourDetail = lazy(() => import("@/pages/TourDetail"))
const Login = lazy(() => import("@/pages/auth/Login"))
const Register = lazy(() => import("@/pages/auth/Register"))
const Profile = lazy(() => import("@/pages/Profile"))
const MyTours = lazy(() => import("@/pages/MyTours"))
const CreateTour = lazy(() => import("@/pages/CreateTour"))
const MyBookings = lazy(() => import("@/pages/MyBookings"))
const Messages = lazy(() => import("@/pages/Messages"))
const Notifications = lazy(() => import("@/pages/Notifications"))
const GuideProfile = lazy(() => import("@/pages/GuideProfile"))

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"))
const AdminUsers = lazy(() => import("@/pages/admin/Users"))
const AdminGuides = lazy(() => import("@/pages/admin/Guides"))
const AdminTours = lazy(() => import("@/pages/admin/Tours"))
const AdminLogs = lazy(() => import("@/pages/admin/Logs"))

// Loading spinner
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

// Protected route wrapper
function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Guest only route (redirect if logged in)
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <div className="min-h-screen bg-mesh-gradient">
      <Header />
      <main className="container py-6">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/tours/:id" element={<TourDetail />} />
            <Route path="/guides/:id" element={<GuideProfile />} />

            {/* Guest only routes */}
            <Route
              path="/login"
              element={
                <GuestRoute>
                  <Login />
                </GuestRoute>
              }
            />
            <Route
              path="/register"
              element={
                <GuestRoute>
                  <Register />
                </GuestRoute>
              }
            />

            {/* Protected routes - any authenticated user */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <MyBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />

            {/* Guide only routes */}
            <Route
              path="/my-tours"
              element={
                <ProtectedRoute roles={["guide", "admin"]}>
                  <MyTours />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tours/new"
              element={
                <ProtectedRoute roles={["guide", "admin"]}>
                  <CreateTour />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tours/:id/edit"
              element={
                <ProtectedRoute roles={["guide", "admin"]}>
                  <CreateTour />
                </ProtectedRoute>
              }
            />

            {/* Admin only routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/guides"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminGuides />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tours"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminTours />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminLogs />
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

export default App
